<template>
  <div>
    <Head>
      <meta
        name="robots"
        content="noindex"
      >
    </Head>
    <v-container>
      <div
        style="max-width: 600px;"
        class="mt-6 mx-auto mb-2"
      >
        <v-text-field
          label="Use this link to edit your FAQ"
          color="primary"
          outlined
          readonly
          persistent-label
          persistent-hint
          hint="Protip: create a bookmark for this url in your browser by pressing ctrl/cmd + D"
          :value="editUrl"
        >
          <template #append>
            <v-hover v-slot="{ hover }">
              <v-icon
                ref="copyBtn"
                :data-clipboard-text="editUrl"
                :color="hover ? 'primary' : ''"
                @click="() => ''"
              >
                mdi-content-copy
              </v-icon>
            </v-hover>
          </template>
        </v-text-field>
      </div>
    </v-container>

    <faq-editor
      :faq-uuid="faqUuid"
      :lite-slug="liteSlug"
    />

    <v-container>
      <div
        style="max-width: 600px;"
        class="mt-6 mx-auto"
      >
        <div class="text-center">
          <v-btn-toggle
            v-model="copyCode"
            tile
            mandatory
            color="primary"
            group
          >
            <v-btn value="embed">
              Embed
            </v-btn>

            <v-btn value="widget">
              Widget
            </v-btn>
          </v-btn-toggle>
        </div>

        <code-block
          language="html"
          :code="`<script${copyCode === 'embed' ? embedCode : widgetCode}
></script>`"
          @copy-success="() => $emit('snack', 'success', 'Copied to clipboard')"
          @copy-error="() => $emit('snack', 'error', 'Could not copy to clipboard')"
        />
        <p class="text-subtitle-1 mt-4 text-center">
          <span v-if="copyCode === 'embed'">Paste this code where you want the FAQ section to appear</span>
          <span v-if="copyCode === 'widget'">Paste this code on the pages where you want the widget to appear</span>
        </p>

        <v-divider />

        <div
          class="text-center mt-6"
        >
          <v-btn
            depressed
            color="error"
            @click="deleteFaq"
          >
            <v-icon left>
              mdi-alert
            </v-icon>
            Delete FAQ section
          </v-btn>
        </div>
      </div>

      <div
        id="comparison-section"
        class="mx-auto"
        style="margin-top: 190px; max-width: 600px"
      >
        <div class="text-center mb-8">
          <div class="font-weight-medium text-h6 text-uppercase mb-6">
            webcomet lite vs webcomet FAQ
          </div>

          <p class="body-1 mb-6">
            Sign up for webcomet FAQ for free and transfer this FAQ section to your new account by clicking the button below.
          </p>

          <v-btn
            color="primary"
            class="mx-auto"
            :href="registerUrl"
            target="_blank"
          >
            Sign up for webcomet FAQ
          </v-btn>
        </div>

        <v-simple-table
          dark
          fixed-header
          class="mx-auto overflow-auto"
          :style="{
            background: $vuetify.theme.themes.dark.background,
            'border-radius': '4px',
            'white-space': 'nowrap'
          }"
        >
          <template #default>
            <thead>
              <tr>
                <th class="text-left">
                  Features
                </th>
                <th class="text-left">
                  webcomet lite
                </th>
                <th class="text-left">
                  webcomet FAQ
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in featuresComparison"
                :key="row.feat"
              >
                <td>{{ row.feat }}</td>
                <td>
                  <v-icon :color="row.lite ? 'success' : 'error'">
                    {{ row.lite ? 'mdi-check' : 'mdi-close' }}
                  </v-icon>
                </td>
                <td>
                  <v-icon :color="row.full ? 'success' : 'error'">
                    {{ row.full ? 'mdi-check' : 'mdi-close' }}
                  </v-icon>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
    </v-container>

    <portal to="widget">
      <webcomet-faq-widget
        :key="`widget-lite-${widgetKey}`"
        :faq-id="faqUuid"
        :disable-statistics="true"
        toggle-label="This is your FAQ section used as a widget"
      />
    </portal>
  </div>
</template>

<script>
import CodeBlock from '@/components/shared/CodeBlock';
import FaqEditor from '@/lite/components/FaqEditor';

export default {
  components: {
    FaqEditor,
    CodeBlock
  },

  props: {
    liteSlug: {
      type: String,
      required: true
    },
    faqUuid: {
      type: String,
      required: true
    },
    editUrl: {
      type: String,
      required: true
    },
    registerUrl: {
      type: String,
      required: true
    },
    loaderScriptUrl: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      widgetKey: 0,
      copyCode: 'embed',
      clipboard: null,
    }
  },

  computed: {
    embedCode() {
      return `
  src="${this.loaderScriptUrl}"
  data-component="webcomet-faq"
  data-faq-id="${this.faqUuid}"`;
    },

    widgetCode() {
      return `
  src="${this.loaderScriptUrl}"
  data-component="webcomet-faq-widget"
  data-faq-id="${this.faqUuid}"
  data-toggle-label="This is your FAQ section used as a widget"
  data-primary-color="#009688"`;
    },

    featuresComparison() {
      return [
        { feat: 'Free-to-use license', lite: true, full: true },
        { feat: 'Premium license', lite: false, full: true },
        { feat: 'Styling & CSS', lite: false, full: true },
        { feat: 'Categories & icons', lite: false, full: true },
        { feat: 'Multiple FAQ collections', lite: false, full: true },
        { feat: 'FAQ widget', lite: true, full: true },
        { feat: 'Embedded FAQ', lite: true, full: true },
        { feat: 'Stand-alone FAQ', lite: false, full: true },
        { feat: 'FAQ REST API', lite: false, full: true },
        { feat: 'Multilingual support', lite: false, full: true },
        { feat: 'Statistics & analysis', lite: false, full: true },
        { feat: 'Team collaboration', lite: false, full: true },
        { feat: 'Web accessibility (WCAG)', lite: true, full: true },
        { feat: 'User feedback', lite: false, full: true },
        { feat: 'Data driven sorting', lite: false, full: true }
      ]
    }
  },

  created() {
    window.eventBus.$on('widget:update', () => {
      this.widgetKey++;
    });
  },

  mounted() {
    this.clipboard = new window.ClipboardJS(this.$refs.copyBtn.$el);

    this.clipboard.on('success', e => {
      this.$emit('snack', 'success', 'Copied to clipboard');
      this.$nextTick(() => e.clearSelection());
    });

    this.clipboard.on('error', () => {
      this.$emit('snack', 'error', 'Could not copy to clipboard');
    });
  },

  methods: {
    deleteFaq() {
      if (window.confirm('Are you sure you want to delete this FAQ section?')) {
        const url = this.route('faq.lite.destroy', { liteSlug: this.liteSlug });

        this.$inertia.delete(url);
      }
    },
  }
}
</script>

<style>
  .v-data-table__wrapper {
    border-radius: 4px !important;
  }

  pre, code {
    background: rgb(30, 26, 25) !important;
    border-radius: 4px;
    overflow-x: scroll !important;
    white-space: pre !important;
  }
</style>